import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronRight, Loader2, Plus, Sparkles, Star } from "lucide-react";
import NewSunoVoiceDialog from "@/components/sunovoice/NewSunoVoiceDialog";
import VoiceStatusChip from "@/components/sunovoice/VoiceStatusChip";
import useSunoVoices from "@/components/sunovoice/useSunoVoices";
import VoiceHealthBanner from "@/components/sunovoice/VoiceHealthBanner";

const STEPS = [
  ["Your singing", "Record or upload yourself singing, then pick the clearest stretch."],
  ["Check phrase", "ELPM writes a short phrase — sing it back so it knows the voice is yours."],
  ["Pick it", "Your voice appears in the Model menu on Generate Song."],
];

function SunoVoiceCard({ v }) {
  return (
    <Link to={`/create/my-voices/elpm/${v.id}`} data-testid={`suno-voice-card-${v.id}`}
      className="group flex flex-col rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/80 p-5 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-cyan-300/30">
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-gradient-to-br from-amber-200 via-cyan-300 to-emerald-400 text-black"><Sparkles className="h-5 w-5" /></span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-display text-[16px] font-semibold">{v.name}</span>
          <span className="block truncate text-[12px] text-white/45">{v.style || "ELPM voice"}</span>
        </span>
        {v.is_default ? <Star data-testid={`suno-voice-card-default-${v.id}`} className="h-4 w-4 fill-cyan-300 text-cyan-300" /> : null}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <VoiceStatusChip status={v.status} testId={`suno-voice-card-status-${v.id}`} />
        {v.is_default ? <span className="rounded-full bg-cyan-300/15 px-2.5 py-1 text-[12px] text-cyan-100">Default for new songs</span> : null}
      </div>
      <span className="mt-4 inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.16em] text-white/50 group-hover:text-white">Open <ChevronRight className="h-3 w-3 transition-transform group-hover:translate-x-1" /></span>
    </Link>
  );
}

/** My Voices, top section — voices ELPM learns from your real singing (listed before the older voices). */
export default function SunoVoicesSection() {
  const { voices } = useSunoVoices();
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(params.get("new") === "1");
  useEffect(() => { if (params.get("new")) setParams({}, { replace: true }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <section data-testid="suno-voices-section" className="space-y-4">
      <VoiceHealthBanner voices={voices} />
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300/80">ELPM voices · recommended</p>
        <h2 className="mt-2 font-display text-base font-semibold md:text-lg">ELPM learns your real singing voice</h2>
      </div>
      <ol className="grid gap-3 sm:grid-cols-3">
        {STEPS.map(([t, b], i) => (
          <li key={t} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
            <span className="font-mono text-[11px] text-cyan-300">0{i + 1}</span>
            <div className="mt-1 text-[14px] font-semibold">{t}</div>
            <p className="mt-1 text-[12px] leading-relaxed text-white/50">{b}</p>
          </li>
        ))}
      </ol>
      {voices === null ? <Loader2 className="h-6 w-6 animate-spin text-white/40" /> : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="suno-voice-grid">
          {voices.map((v) => <SunoVoiceCard key={v.id} v={v} />)}
          <button type="button" data-testid="suno-voice-new" onClick={() => setOpen(true)}
            className="flex min-h-[170px] flex-col items-center justify-center gap-2 rounded-3xl border-2 border-dashed border-cyan-300/25 text-white/70 transition-colors hover:border-cyan-300/60 hover:text-white">
            <Plus className="h-6 w-6" /><span className="font-display text-[15px] font-semibold">{voices.length ? "New ELPM voice" : "Teach ELPM my voice"}</span>
          </button>
        </div>
      )}
      <NewSunoVoiceDialog open={open} onClose={() => setOpen(false)} />
    </section>
  );
}
